import React, { useState, useEffect, useRef } from 'react';
import NavBar from './NavBar'; // Assuming NavBar.js is in the same directory
import ExpandableButtons from './TableButtons'; // Assuming TableButtons.js is in the same directory
import SuggestedQuestions from './SuggestedQuestions'; // Assuming SuggestedQuestions.js is in the same directory
import ChatInput from './ChatInput'; // Assuming ChatInput.js is in the same directory
import { Paper, Button } from '@mui/material'; // Ensure Button is imported for the "View Table" button
import tableLabels from '../constants/tableLabels'; // Assuming this path is correct
import TablePopup from './TablePopup'; // Assuming TablePopup.js is in the same directory
// import VoiceInteractionModal from './VoiceInteractionModal'; // Assuming VoiceInteractionModal.js is in the same directory
import { getAllMessages, saveMessage } from '../utils/chatDB'; // Assuming chatDB.js is in ../utils/
import ThinkingBubble from './ThinkingBubble'; // Assuming ThinkingBubble.js is in the same directory
import ChatHistoryPanel from './ChatHistoryPanel'; // Assuming ChatHistoryPanel.js is in the same directory
import DomainSelectionPopup from './DomainSelectionPopup'; 
import AnalyticsPopup from './AnalyticsPopup';
import ChatChart from './ChatChart';
import { speakText } from '../utils/playAudioResponse';
import PurchaseRequisitionPopup from './PurchaseRequisitionPopup';
import PurchaseDashboard from './PurchaseDashboard';
import FinanceDashboard from './FinanceDashboard';
import PetronasDashboard from './PetronasDashboard';
import TauraniDashboard from './TauraniDashboard';
import { FINANCE_COMPANY } from '../constants/financeCompany';
import PalmtopDashboard from './PalmtopDashboard';
import PurchaseOptimizer from './PurchaseOptimizer';
// import SupplierInvoiceUpload from './SupplierInvoiceUpload'; // Add this at the top with other imports

// import { Button } from '@mui/material'; // Make sure this import exists at the top



// --- Post-processing function (Updated) ---
const MAX_PARAGRAPH_LENGTH = 350; // Assuming this is defined outside or at the top of your file scope

// Helper function to render a parsed table into an HTML string
function renderTable(headers, rows, alignments) {
    if (!headers || headers.length === 0) return ""; // Don't render if no headers

    let tableHtml = "<table border='1' style='border-collapse: collapse; width: auto; margin-top: 10px; margin-bottom: 10px;'><thead><tr>";
    headers.forEach((header, index) => {
        const align = alignments[index] || 'left';
        tableHtml += `<th style='text-align: ${align}; padding: 6px; border: 1px solid #555; background-color: #3a3a3c;'>${header}</th>`;
    });
    tableHtml += "</tr></thead><tbody>";
    rows.forEach(row => {
        tableHtml += "<tr>";
        // Ensure row matches header length for consistency, padding with empty cells if necessary
        for (let i = 0; i < headers.length; i++) {
            const cell = row[i] !== undefined ? row[i] : "";
            const align = alignments[i] || 'left';
            tableHtml += `<td style='text-align: ${align}; padding: 6px; border: 1px solid #555;'>${cell}</td>`;
        }
        tableHtml += "</tr>";
    });
    tableHtml += "</tbody></table>";
    return tableHtml;
}

// Helper function to detect and convert Markdown tables to HTML
function processMarkdownTables(text) {
    const lines = text.split('\n');
    const newContent = [];
    let i = 0;

    const tableRowRegex = /^\|(.+)\|$/;
    // Regex to identify a markdown table separator line like |---|---| or |:--|--:| etc.
    // It ensures each cell part of the separator has hyphens.
    const tableSeparatorRegex = /^\|(?:\s*:?-{2,}:?\s*\|)+$/;


    while (i < lines.length) {
        const currentLineOriginal = lines[i];
        const currentLineTrimmed = currentLineOriginal.trim();
        const nextLineTrimmed = (i + 1 < lines.length) ? lines[i+1].trim() : null;

        // Check for table header: | content | followed by separator |---|
        if (tableRowRegex.test(currentLineTrimmed) && nextLineTrimmed && tableSeparatorRegex.test(nextLineTrimmed)) {
            const potentialHeaderCells = currentLineTrimmed.split('|').slice(1, -1).map(h => h.trim());
            const potentialSeparatorCells = nextLineTrimmed.split('|').slice(1, -1).map(s => s.trim());

            // Basic validation: header and separator should have same number of cells, and at least one cell.
            if (potentialHeaderCells.length > 0 && potentialHeaderCells.length === potentialSeparatorCells.length) {
                const tableLines = [currentLineTrimmed, nextLineTrimmed];
                let k = i + 2;
                while (k < lines.length && tableRowRegex.test(lines[k].trim())) {
                    tableLines.push(lines[k].trim());
                    k++;
                }

                // Parse the confirmed table
                const tableHeader = potentialHeaderCells;
                const columnAlignments = potentialSeparatorCells.map(cell => {
                    const c = cell.trim();
                    if (c.startsWith(':') && c.endsWith(':')) return 'center';
                    if (c.endsWith(':')) return 'right'; // Markdown usually specifies left align with :---
                    if (c.startsWith(':')) return 'left';
                    return 'left'; // Default alignment
                });
                const tableRowsData = tableLines.slice(2).map(rLine => rLine.split('|').slice(1, -1).map(cell => cell.trim()));

                newContent.push(renderTable(tableHeader, tableRowsData, columnAlignments));
                i = k; // Move index past this table
                continue; // Continue to next line after table processing
            }
        }
        
        // If not a table or table structure is invalid, push the original line
        newContent.push(currentLineOriginal);
        i++;
    }
    return newContent.join('\n');
}

// Helper function to apply the rest of the text processing rules
function applyStandardProcessing(textChunk) {
    if (textChunk === null || textChunk === undefined) return "";
    let text = String(textChunk); // Ensure it's a string

    // Trim leading/trailing newlines that might result from splits, but preserve internal structure for now.
    // The .trim() here is important for segments.
    text = text.trim();
    if (!text) return "";


    // Paragraph splitting and length management (from your original code)
    // This should operate on text that has had its escaped newlines normalized.
    text = text.replace(/\\r\\n|\\r|\\n/g, '\n'); // Normalize newlines first for consistent splitting.

    const initialParagraphs = text.split('\n');
    const processedParagraphs = [];
    for (const p of initialParagraphs) {
        let currentSegment = p;
        if (currentSegment.length > MAX_PARAGRAPH_LENGTH) {
            const sentences = currentSegment.match(/[^.!?]+[.!?]*\s*|[^.!?]+$/g) || [];
            let reconstructedParagraph = "";
            const tempSegments = [];
            for (const sentence of sentences) {
                if ((reconstructedParagraph + sentence).length > MAX_PARAGRAPH_LENGTH && reconstructedParagraph) {
                    tempSegments.push(reconstructedParagraph.trim());
                    reconstructedParagraph = sentence.trimStart();
                } else {
                    reconstructedParagraph += sentence;
                }
            }
            if (reconstructedParagraph) {
                tempSegments.push(reconstructedParagraph.trim());
            }
            processedParagraphs.push(...tempSegments.filter(seg => seg.length > 0));
        } else {
            processedParagraphs.push(currentSegment);
        }
    }
    text = processedParagraphs.join('\n');

    // Bold processing
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Triple-backtick code block processing
    text = text.replace(/```([\s\S]*?)```/g, (match, p1) => {
        // Escape HTML entities inside the code block to prevent them from being rendered as HTML
        const escapedCode = p1.replace(/&/g, '&amp;')
                              .replace(/</g, '&lt;')
                              .replace(/>/g, '&gt;')
                              .replace(/"/g, '&quot;')
                              .replace(/'/g, '&#039;');
        return `<pre><code style="display: block; white-space: pre-wrap; background-color: #2d2d2d; color: #f0f0f0; padding: 10px; border-radius: 5px; margin-top: 10px; margin-bottom: 10px; overflow-x: auto;">${escapedCode.trim()}</code></pre>`;
    });


    // Asterisk list processing
    const lines = text.split('\n');
    const newLines = [];
    let inAsteriskList = false;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim(); // Trim here for list detection
        // Accept both markdown bullet markers: "* item" and "- item".
        const bulletMatch = trimmedLine.match(/^[*-]\s+(.*)$/);
        if (bulletMatch) {
            const listItemText = bulletMatch[1].trim();
            // Re-apply bold to list item text if not already captured (e.g. **text** inside * list item)
            const processedListItemText = listItemText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            const listItem = `<li>${processedListItemText}</li>`;
            if (!inAsteriskList) {
                newLines.push('<ul>');
                inAsteriskList = true;
            }
            newLines.push(listItem);
        } else {
            if (inAsteriskList) {
                newLines.push('</ul>');
                inAsteriskList = false;
            }
            newLines.push(line); // Push original line if not a list item
        }
    }
    if (inAsteriskList) {
        newLines.push('</ul>');
    }
    text = newLines.join('\n');

    // Comma list processing
    const commaProcessedLines = text.split('\n').map(line => {
        const trimmedLine = line.trim();
        // Protect already formed HTML list items and strong tags
        if (trimmedLine.startsWith('<li') || trimmedLine.startsWith('<ul') || trimmedLine.startsWith('</ul') || trimmedLine.match(/^<strong>.*<\/strong>$/i) || trimmedLine.startsWith('<p>')) {
            return line;
        }
        const items = trimmedLine.split(',');
        const wordCount = trimmedLine.split(/\s+/).length;
        if (items.length >= 3 && wordCount <= 15 && !trimmedLine.endsWith('.')) {
            return '<ul>' + items.map(item => {
                const trimmedItem = item.trim();
                const processedItem = trimmedItem.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                return `<li>${processedItem}</li>`;
            }).join('') + '</ul>';
        }
        return line;
    });
    text = commaProcessedLines.join('\n');

    // Clean up list HTML
    text = text.replace(/<\/ul>\n/g, '</ul>').replace(/\n<ul>/g, '<ul>');
    text = text.replace(/<\/li>\n/g, '</li>').replace(/\n<li/g, '<li');

    // Convert remaining newlines to <br />, carefully avoiding those inside existing HTML tags or structure.
    // This regex is crucial. It should only add <br /> for newlines that are actual line breaks in paragraphs.
    // It avoids adding <br /> after a block element like </ul> or before one like <ul>.
    // It also avoids adding <br /> if the line is empty or part of other HTML.
    text = text.replace(/\n(?!<\/?(ul|li|strong|p|div|h\d|br|table|thead|tbody|tr|th|td))(?=[^\s<])/g, '<br />');
    // Remove leading/trailing <br /> tags that might appear due to processing
    text = text.replace(/^<br\s*\/?>|<br\s*\/?>$/g, '');


    return text;
}

function postProcessText(inputText) {
    if (typeof inputText !== 'string') {
        return inputText || ""; // Return empty string for null/undefined if not string
    }

    let text = inputText;

    text = text.replace(/final_response :/g, "");

    // 1. Process Markdown tables into HTML
    text = processMarkdownTables(text);

    // 2. Split the text by HTML tables and process non-table parts
    // The regex for splitting keeps the table HTML as part of the array.
    // It looks for <table...> ... </table>, non-greedy.
    const parts = text.split(/(<table(?:.|\n)*?<\/table>)/g);
    const processedParts = [];

    for (const part of parts) {
        if (part === null || part === undefined) continue;

        const trimmedPart = part.trim();
        if (trimmedPart.startsWith("<table") && trimmedPart.endsWith("</table>")) {
            processedParts.push(part); // Add HTML table as is (it's already a string)
        } else if (trimmedPart.length > 0) {
            // Apply standard processing to non-table text parts
            processedParts.push(applyStandardProcessing(part));
        } else if (part.length > 0 && trimmedPart.length === 0) {
            // Preserve whitespace-only parts if they were not just for table separation
            // This might be newlines between a table and text. applyStandardProcessing handles internal newlines.
            processedParts.push(part);
        }
    }

    text = processedParts.join('');

    return text;
}

// --- End of Post-processing function ---
// --- End of Post-processing function ---
// Helper function to render file links from files_dict
// --- Make sure Button is imported at the top ---
// import { Button } from '@mui/material';

// --- NEW renderFileLinks function ---
function renderFileLinks(filesDict) {
    if (!filesDict || !Array.isArray(filesDict) || filesDict.length === 0) {
        return null; // Return null if no files
    }

    const fileButtons = filesDict.map((fileObj, index) => {
        const fileName = Object.keys(fileObj)[0];
        const fileUrl = fileObj[fileName];

        // Creates a React MUI Button component for each file
        return (
            <Button
                key={index}
                variant="contained"
                size="small"
                component="a"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none', margin: '5px' }}
            >
                {fileName}
            </Button>
        );
    });

    // Returns a React div containing the Button components
    return <div style={{ marginTop: '10px' }}>{fileButtons}</div>;
}

// Function to process the file response from /filerag API
// --- NEW processFileResponse function ---
// Function to process the file response from /filerag API
// --- UPDATED processFileResponse function ---
function processFileResponse(responseData) {
    let fileElements = null; // Will hold React elements for buttons
    let answerHtml = "";

    // Generate file button elements (remains the same)
    if (responseData.files_dict && Array.isArray(responseData.files_dict)) {
        fileElements = renderFileLinks(responseData.files_dict);
    }

    // Process the answer string
    if (responseData.answer && typeof responseData.answer === 'string') {
        let rawAnswer = responseData.answer; // Get the raw answer string

        // --- ADD CLEANUP STEP HERE ---
        const answerMarker = " ,answer: ";
        const markerIndex = rawAnswer.indexOf(answerMarker);

        if (markerIndex !== -1) {
            // If ",answer: " is found, take only the text AFTER it
            rawAnswer = rawAnswer.substring(markerIndex + answerMarker.length);
        } // Otherwise, use the rawAnswer as is (in case the prefix isn't always there)
        // --- END CLEANUP STEP ---

        // Now pass the CLEANED answer string to postProcessText
        answerHtml = postProcessText(rawAnswer.trim()); // Also trim whitespace

    } else {
        answerHtml = "<p>No answer provided.</p>"; // Fallback remains the same
    }

    // Return the object with button elements and cleaned/processed answer HTML
    return {
        files: fileElements,
        answer: answerHtml
    };
}



// Keywords that identify "thinking" or progress messages from the SSE stream (from your original file)
const THINKING_KEYWORDS = [
    "Getting Schema",
    "Getting Data",
    "Searching Relevant Info",
    "Finalizing Answer",
    "Processing request",
    "Analyzing data"
];

// Simple conversation ID generator
const generateNewConversationId = () => `chat_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const ChatInterface = ({ themeMode, onToggleTheme }) => {
  const domainList = Object.keys(tableLabels);
  // const [domain, setDomain] = useState(domainList[0] || '');
  const [domain, setDomain] = useState('');
  const [selectedTable, setSelectedTable] = useState(
    domainList.length > 0 && tableLabels[domainList[0]] && tableLabels[domainList[0]].length > 0
      ? tableLabels[domainList[0]][0].full
      : ''
  );
  const [messages, setMessages] = useState([]);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]); // For ThinkingBubble
  const messagesEndRef = useRef(null);
  const [tablePopup, setTablePopup] = useState({ open: false, data: null });
  const [analyticsPopup, setAnalyticsPopup] = useState({ open: false, data: null, title: '', spec: null, columns: null });
  // const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [isVoiceModeActive, setIsVoiceModeActive] = useState(false);
  const [shouldTurnOffVoice, setShouldTurnOffVoice] = useState(false);

  const [conversationId, setConversationId] = useState(generateNewConversationId());

   const [showDomainPopup, setShowDomainPopup] = useState(false);


  // State for Chat History Panel
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const hardcodedUserId = 'i319'; // This should ideally be dynamic (e.g., from auth context)
  const [prPopup, setPrPopup] = useState({ open: false, data: null, onSubmit: null });
  const [dashboardOpen, setDashboardOpen] = useState(false);

  // Function to toggle history panel
  const handleToggleHistoryPanel = () => {
    setIsHistoryPanelOpen(!isHistoryPanelOpen);
  };

  // Effect to load messages when conversationId changes
  useEffect(() => {
    if (conversationId) {
      getAllMessages(conversationId) // Assumes getAllMessages is adapted for conversationId
        .then(stored => {
          if (stored && stored.length > 0) {
            const validStoredMessages = stored.map(msg => {
              let textContent = msg.text;
              if (typeof textContent !== 'string') {
                try { textContent = String(textContent); }
                catch (e) { textContent = "Error: Unable to display message content."; }
              }
              // Ensure tableData is preserved if it exists
              return { ...msg, text: textContent, isHtml: msg.isHtml || false, tableData: msg.tableData || null };
            });
            setMessages(validStoredMessages);
            setConversationStarted(true);
          } else {
            setMessages([]);
            setConversationStarted(false); // This ensures SuggestedQuestions view is shown for new chats
          }
          setThinkingSteps([]); // Clear thinking steps when conversation changes
        })
        .catch(error => {
          console.error("Error loading messages for conversation:", conversationId, error);
          setMessages([{ text: "Error loading messages for this chat.", sender: 'bot', isError: true, isHtml: false, conversationId: conversationId }]);
          setConversationStarted(true);
          setThinkingSteps([]);
        });
    }
  }, [conversationId]);

  // Effect to scroll to the bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, thinkingSteps]); // Also trigger on thinkingSteps change for better scroll behavior

  // Effect to update selected table when domain changes
  useEffect(() => {
    if (domain && tableLabels[domain]) {
      setSelectedTable(tableLabels[domain].length > 0 ? tableLabels[domain][0].full : '');
    } else if (!domain) {
        setSelectedTable('');
    }
  }, [domain]);


  // ADD THIS ENTIRE useEffect BLOCK:
  useEffect(() => {
    const appOpenedBefore = localStorage.getItem('appOpenedBefore');
    const previouslySelectedDomain = localStorage.getItem('selectedDomain');

    if (!appOpenedBefore || !previouslySelectedDomain) {
      // If it's the very first time or no domain was ever stored, show popup
      setShowDomainPopup(true);
      if (!appOpenedBefore) {
        localStorage.setItem('appOpenedBefore', 'true'); // Mark that app has been opened
      }
    } else {
      // If a domain was previously selected, set it
      setDomain(previouslySelectedDomain);
      // If you have a specific function that needs to run when a domain is set from storage
      // (like initializing conversationId if it's not handled by domain change effect),
      // you might call it here. For now, we assume the existing useEffect watching `domain`
      // or `conversationId` handles subsequent logic.
    }
  }, []);

  // Handler for domain change - generates a new conversation ID
  const handleDomainChange = (newDomain) => {
    setDomain(newDomain);
     localStorage.setItem('selectedDomain', newDomain);
    const newId = generateNewConversationId();
    setConversationId(newId);
  };

  // Handler for "New Chat" button - generates a new conversation ID
  const handleNewChat = () => {
    const newId = generateNewConversationId();
    setConversationId(newId);
  };

  // Handler for table selection
  const handleTableSelect = (table) => {
    setSelectedTable(table);
  };

const handlePurchaseRequisition = async (messageText, conversationId) => {
    // This nested function handles the final data submission from the popup
    const handlePrSubmit = async (prDataFromPopup) => {
      const submitUrl = 'https://cd9d-205-254-163-120.ngrok-free.app/submit-pr';

      try {
        // FIX: Check if the data is an array. If not, wrap it in one.
        // This ensures the body always matches the backend's expected List[PRData].
        const payload = Array.isArray(prDataFromPopup) ? prDataFromPopup : [prDataFromPopup];

        const response = await fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pr_data: payload }),
        });

        const result = await response.json();
        // The backend answers 200 even when SAP rejects the requisition, with the
        // reason in `message` and no `purchase_requisition`. Keying only off
        // response.ok therefore rendered SAP failures as successes, so treat a
        // missing PR number as a failure too. A 422 has neither, and its
        // `detail` carries the validation reason instead of `message`.
        if (!response.ok || !result.purchase_requisition) {
          const reason = result.message
            || (Array.isArray(result.detail) ? result.detail.map(d => d.msg).join(', ') : result.detail)
            || 'Failed to create the Purchase Requisition.';
          throw new Error(reason);
        }

        // `note` is set when the request carried a delivery date: the SAP
        // service has no field for one, so it was not stored. Surfacing it stops
        // the user assuming the date was applied.
        const successMessage = {
          text: `${result.message || 'Purchase Requisition created successfully!'}`
            + (result.purchase_requisition ? ` PR number: ${result.purchase_requisition}.` : '')
            + (result.note ? ` Note: ${result.note}` : ''),
          sender: 'bot',
          isHtml: false,
          conversationId,
        };
        setMessages(prev => [...prev, successMessage]);
        saveMessage(successMessage, conversationId);

      } catch (error) {
        console.error('Failed to submit Purchase Requisition:', error);
        const errorMessage = { text: `Submission failed: ${error.message}`, sender: 'bot', isError: true, isHtml: false, conversationId };
        setMessages(prev => [...prev, errorMessage]);
        saveMessage(errorMessage, conversationId);
      } finally {
        setPrPopup({ open: false, data: null, onSubmit: null });
      }
    };

    // Main logic: This part remains mostly the same
    setIsThinking(true);

    // /pr_ai is a plain JSON POST, not an SSE stream, so it never emits the
    // "thinking" steps the other agents stream. The ThinkingBubble only renders
    // when thinkingSteps is non-empty, so seed the steps here (staggered) so the
    // PR agent shows the same loading indicator while the request is in flight.
    setThinkingSteps(['Processing request']);
    const prThinkingTimers = [
      setTimeout(() => setThinkingSteps(prev => [...prev, 'Searching Relevant Info']), 600),
      setTimeout(() => setThinkingSteps(prev => [...prev, 'Analyzing data']), 1400),
      setTimeout(() => setThinkingSteps(prev => [...prev, 'Finalizing Answer']), 2400),
    ];

    try {
      const queryUrl = 'https://cd9d-205-254-163-120.ngrok-free.app/pr_ai';

      const response = await fetch(queryUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: messageText,
          conversation_id: conversationId,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Server error: ${response.status}`);
      }
      
      const responseJson = await response.json();

      // The `pr_data` can be either an object or an array. We pass it directly to the popup state.
      // The enhanced popup component will know how to render it.
      if (responseJson.pr_data) {
        setPrPopup({
          open: true,
          data: responseJson.pr_data,
          onSubmit: handlePrSubmit,
        });
      } 
      else if (responseJson.message) {
        // The PR agent answers in Markdown (**bold**, "- " bullets), so run it
        // through the same formatter the streaming agents use instead of
        // dumping the raw text into the bubble.
        const botMessage = { text: postProcessText(responseJson.message), sender: 'bot', isHtml: true, conversationId };
        setMessages(prev => [...prev, botMessage]);
        saveMessage(botMessage, conversationId);
      } 
      else {
        throw new Error("Received an unexpected JSON format from the server.");
      }

    } catch (error) {
      console.error('Error during Purchase Requisition query:', error);
      const errorMessage = { text: `An error occurred: ${error.message}`, sender: 'bot', isError: true, isHtml: false, conversationId };
      setMessages(prev => [...prev, errorMessage]);
      saveMessage(errorMessage, conversationId);
    } finally {
      // Stop any pending step timers and clear the steps so a late timer can't
      // push a "Finalizing Answer" line after the answer is already shown.
      prThinkingTimers.forEach(clearTimeout);
      setThinkingSteps([]);
      setIsThinking(false);
    }
  };

  // Handler for sending a message
  const handleSendMessage = async (messagePayload) => {
    const textForUserMessage = typeof messagePayload === 'string' ? messagePayload : messagePayload.text;
    const isVoiceInput = typeof messagePayload === 'object' && messagePayload.isVoiceInput === true;
    const shouldAutoSpeak = isVoiceInput || isVoiceModeActive;
    const isTableForAPI = typeof messagePayload === 'object' && messagePayload.isTable !== undefined ? messagePayload.isTable : false;

    if (typeof textForUserMessage !== 'string') {
        console.error("Invalid message text type for user message:", textForUserMessage);
        return;
    }

    const userMessage = { text: textForUserMessage, sender: 'user', isHtml: false, conversationId: conversationId , isVoiceInput: isVoiceInput};
    setMessages(prev => [...prev, userMessage]);
    saveMessage(userMessage, conversationId);
    if (!conversationStarted) setConversationStarted(true);

    setIsThinking(true);
    setThinkingSteps([]);
    try {
      let message_url = 'https://cd9d-205-254-163-120.ngrok-free.app/query';
       if (domain === 'Purchase Requisition') {
        await handlePurchaseRequisition(textForUserMessage, conversationId);
        return;
       }
      else if (domain === 'Human Resource') {
        message_url = 'https://cd9d-205-254-163-120.ngrok-free.app/ZOHO_HRv1';
      }
      else if (domain === 'Policy Repository') {
        message_url ='https://cd9d-205-254-163-120.ngrok-free.app/file_rag';
      }
      else if (domain === 'Palmtop') {
        message_url = 'https://cd9d-205-254-163-120.ngrok-free.app/palmtop';
      }
      else if (domain === 'Finance Analyst') {
        message_url = 'https://cd9d-205-254-163-120.ngrok-free.app/finance_analyst';
      }

      const requestBody = {
        message: textForUserMessage,
        user_id: hardcodedUserId,
        module_name: domain,
        table_name: selectedTable,
        user_name: 'Pradhan',
        isTable: isTableForAPI,
        conversation_id: conversationId,
      };

      const response = await fetch(message_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
      }
      if (!response.body) throw new Error('ReadableStream not supported.');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let rawBotAnswerParts = '';
      let receivedTablePayload = null;
      let fileResponseData = null;

      // The table payload is a single long JSON line, and a network chunk can
      // land in the middle of it. Hold the trailing partial line back until the
      // next chunk completes it, otherwise JSON.parse fails and the raw payload
      // is shown to the user as text.
      let pending = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          pending += decoder.decode();
        } else {
          pending += decoder.decode(value, { stream: true });
        }
        const sseLines = pending.split('\n');
        pending = done ? '' : sseLines.pop();

        for (const sseLine of sseLines) {
          if (sseLine.startsWith('data: ')) {
            const eventDataString = sseLine.substring(6).trim();
            if (!eventDataString) continue;

            const isThinkingMessage = THINKING_KEYWORDS.some(keyword => eventDataString.includes(keyword));

            if (isThinkingMessage) {
              setThinkingSteps(prevSteps => [...prevSteps, eventDataString]);
            } else {
              try {
                const jsonData = JSON.parse(eventDataString);
                if (domain === 'Policy Repository' && jsonData.files_dict) {
                        fileResponseData = jsonData; // Store file response
                }
                else if (jsonData.format_number === 4 && jsonData.table_data) {
                  receivedTablePayload = jsonData;
                  if (jsonData.answer && typeof jsonData.answer === 'string') {
                    rawBotAnswerParts += jsonData.answer + '\n';
                  }
                } else if (jsonData.answer && typeof jsonData.answer === 'string') {
                  rawBotAnswerParts += jsonData.answer + '\n';
                } else if (jsonData.message && typeof jsonData.message === 'string') {
                  rawBotAnswerParts += jsonData.message + '\n';
                } else if (typeof jsonData === 'string') {
                    rawBotAnswerParts += jsonData + '\n';
                }
              } catch (e) {
                rawBotAnswerParts += eventDataString + '\n';
              }
            }
          }
        }

        if (done) break;
      }
      setIsThinking(false);
            if (domain === 'Policy Repository' && fileResponseData) {
          // --- CORRECTED BLOCK START ---
          // Call the processFileResponse, gets an OBJECT { files: ReactElements, answer: HtmlString }
          const processedData = processFileResponse(fileResponseData);

          // Create the CORRECT message object structure
          const botFileMessage = {
              fileElements: processedData.files, // Store the React Button elements here
              answerHtml: processedData.answer, // Store the answer HTML string here
              sender: 'bot',
              isHtml: true, // Still true because answerHtml needs it
              isFileMessage: true, // <<< ADD THIS FLAG!
              conversationId: conversationId,
          };
          setMessages(prev => [...prev, botFileMessage]); // Adds the correctly structured message

          // Adjust saveMessage (Example: Save a simplified version)
          saveMessage({
              // Example: Create a simple text representation for storage if needed
              text: `Policy Repository: ${fileResponseData.files_dict?.map(f => Object.keys(f)[0]).join(', ') || 'N/A'}\nAnswer: ${fileResponseData.answer || 'N/A'}`,
              sender: 'bot',
              isHtml: false, // Store as plain text in DB
              // Optionally store flags if needed for reloading, e.g., isFileMessage: true
              conversationId: conversationId,
          }, conversationId);
        }
    else{
      const processedApiText = postProcessText(rawBotAnswerParts.trim());

      if (receivedTablePayload && receivedTablePayload.table_data && receivedTablePayload.format_number === 4) 
 {
        let rowsForPopup = [];
        const tableDataFromPayload = receivedTablePayload.table_data;
        // More robust handling of tableDataFromPayload
        if (tableDataFromPayload && typeof tableDataFromPayload === 'object' && Object.keys(tableDataFromPayload).length > 0) {
            const keys = Object.keys(tableDataFromPayload);
            if (tableDataFromPayload[keys[0]] && Array.isArray(tableDataFromPayload[keys[0]])) {
                const numRows = tableDataFromPayload[keys[0]].length;
                if (numRows > 0) {
                    rowsForPopup = Array.from({ length: numRows }, (_, rowIndex) => {
                        const row = {};
                        keys.forEach(key => {
                            row[key] = (tableDataFromPayload[key] && tableDataFromPayload[key][rowIndex] !== undefined) ? tableDataFromPayload[key][rowIndex] : null;
                        });
                        return row;
                    });
                } else {
                     console.warn("Table data (format 4) has columns but no rows:", tableDataFromPayload);
                }
            } else {
                 console.warn("Table data (format 4) structure issue: first key does not point to an array or is empty.", tableDataFromPayload);
            }
        } else {
            console.warn("Table data (format 4) is missing, not an object, or empty:", tableDataFromPayload);
        }
        // rowsForPopup will be [] if issues are found, which is a valid state for TablePopup & downloadCSV
        
            const botTableMessage = {
              text: processedApiText || 'Here is what the data shows.',
              sender: 'bot',
              showTableButton: true, // Existing flag for table button
              tableData: rowsForPopup,
              // The server picked the chart from the question and the real
              // column types; carry both so the bubble can draw it straight
              // away and the popup does not have to guess.
              chartSpec: receivedTablePayload.chart || null,
              columnMeta: receivedTablePayload.columns || null,
              isHtml: true,
              conversationId: conversationId,
            };
            setMessages(prev => [...prev, botTableMessage]);
            saveMessage(botTableMessage, conversationId);
        
        
      
      } else if (processedApiText.trim()) {
        const botTextMessage = { text: processedApiText, sender: 'bot', isHtml: true, conversationId: conversationId };
       if ((shouldAutoSpeak ||  messagePayload?.isVoiceInput ) && processedApiText.trim()) {
      
        // Convert HTML to plain text for speech
        const plainTextForSpeech = processedApiText
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
      
      // Speak the response
        try {
          await speakText(plainTextForSpeech);
          if (messagePayload?.isVoiceInput) {
      setShouldTurnOffVoice(true);
      setTimeout(() => setShouldTurnOffVoice(false), 100); // Reset the flag
    }
          
          }  catch (error) {
          console.error('Text-to-speech error:', error);
          }
      }
        setMessages(prev => [...prev, botTextMessage]);
        saveMessage(botTextMessage, conversationId);
      } else if (!receivedTablePayload) {
        const emptyResponseMessage = { text: "Received an empty response from the bot.", sender: 'bot', isHtml: false, conversationId: conversationId };
        setMessages(prev => [...prev, emptyResponseMessage]);
        saveMessage(emptyResponseMessage, conversationId);
      }
    }

    } catch (error) {
      setIsThinking(false);
      setThinkingSteps([]);
      console.error('Error handling message stream:', error);
      const errorMessage = {
        text: `Sorry, there was an error: ${error.message}`,
        sender: 'bot', isError: true, isHtml: false,
        conversationId: conversationId,
      };
      setMessages(prev => [...prev, errorMessage]);
      saveMessage(errorMessage, conversationId);
    }
  };

  

  const handleDomainSelectedFromPopup = (selectedDomainFromPopup) => {
    handleDomainChange(selectedDomainFromPopup); // Re-use existing domain change logic
    // localStorage.setItem('selectedDomain', selectedDomainFromPopup); // This is now handled in handleDomainChange
    setShowDomainPopup(false); // Close the popup
  };

  // Function to download CSV data
  const downloadCSV = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        alert('No data available to download for CSV.');
        return;
    }
    if (typeof data[0] !== 'object' || data[0] === null) {
        alert('CSV data is not in the expected format (array of objects).');
        return;
    }
    try {
      const headers = Object.keys(data[0]);
      if (headers.length === 0) {
          alert('CSV data objects have no properties (headers).');
          return;
      }
      const headerRow = headers.join(',');
      const rows = data.map(row =>
        headers.map(header => {
          const val = row[header] === null || row[header] === undefined ? '' : row[header];
          const strVal = String(val);
          return strVal.includes(',') || strVal.includes('"') ? `"${strVal.replace(/"/g, '""')}"` : strVal;
        }).join(',')
      );
      const csvContent = [headerRow, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'table_data.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Error creating CSV file: ' + error.message);
    }
  };

  const isFinanceDomain = domain === 'Analytics' || domain === 'Finance Analyst';

  return (
     <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'var(--bg)' }}>

      {/* Render NavBar only if a domain is selected AND popup is not shown */}
      
     {domain && !showDomainPopup && (
       <NavBar
         onNewChat={handleNewChat}
         currentConversationId={conversationId}
          domain={domain}
         onDomainChange={handleDomainChange}
         onToggleHistory={handleToggleHistoryPanel}
         onToggleTheme={onToggleTheme}
          themeMode={themeMode}
          onOpenDashboard={() => setDashboardOpen(true)}
       />
     )}

     {/* Both finance agents (Finance Reports and Finance Analyst) get the
         financial-statement dashboard,
         Palmtop gets the backlog manifest, and every other in-app dashboard
         domain gets the purchase one. */}
     {isFinanceDomain && FINANCE_COMPANY === 'taurani' ? (
       <TauraniDashboard
         open={dashboardOpen}
         onClose={() => setDashboardOpen(false)}
       />
     ) : isFinanceDomain && FINANCE_COMPANY === 'petronas' ? (
       <PetronasDashboard
         open={dashboardOpen}
         onClose={() => setDashboardOpen(false)}
       />
     ) : isFinanceDomain ? (
       <FinanceDashboard
         open={dashboardOpen}
         onClose={() => setDashboardOpen(false)}
       />
     ) : domain === 'Palmtop' ? (
       <PalmtopDashboard
         open={dashboardOpen}
         onClose={() => setDashboardOpen(false)}
       />
     ) : (
       <PurchaseDashboard
         open={dashboardOpen}
         domain={domain}
         onClose={() => setDashboardOpen(false)}
       />
     )}


      <DomainSelectionPopup
        open={showDomainPopup}
        domainList={domainList}
        onDomainSelect={handleDomainSelectedFromPopup}
        // Optional: if you want a backdrop click to close, but generally selection is preferred
        // onClose={() => {
        //   // Decide if user can close without selection. If so, what's the default domain?
        //   // For now, we assume selection is mandatory if popup is shown.
        //   // setShowDomainPopup(false);
        // }}
      />


      {/* Purchase Optimizer is a quotation-comparison panel, not a chat: it
          takes the place of the message list and the input box. */}
      {domain === 'Purchase Optimizer' ? (
        <PurchaseOptimizer />
      ) : (
      <>
      <div className="chat-body" style={{ flexGrow: 1, overflowY:'auto', padding:'20px', backgroundColor:'var(--bg)', color:'var(--text)' }}>

        {!conversationStarted && messages.length === 0 && !isThinking ? (
          (
          <SuggestedQuestions domain={domain} selectedTable={selectedTable} onQuestionSelect={handleSendMessage} />
       ) 
      ) : (
          <div className="messages-container">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender}`} style={{ marginBottom: '15px', display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                {msg.sender === 'bot' && (
                  <img src="https://ik.imagekit.io/qumv0ofav/alex_image_lowpx.jpg?updatedAt=1748336452007" alt="Bot" style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '8px', objectFit: 'cover', alignSelf: 'flex-start' }} />
                )}
                <div className="message-bubble"
                  style={{
                    padding: '10px 15px', borderRadius: '18px', maxWidth: '70%',
                    backgroundColor: (t)=> (t.palette.mode==='dark' ? '#2c2c2e' : t.palette.background.paper),
                    color: (t) => (t.palette.mode === 'dark' ? 'white' : 'black'), boxShadow: '0 1px 3px rgba(0,0,0,0.2)', wordBreak: 'break-word', textAlign: 'left',
                  }}>
                         {/* Check if it's our special file message */}
            {msg.isFileMessage ? (
                <>
                    {/* Render the React Button components directly */}
                    {msg.fileElements}
                    {/* Render the answer HTML using dangerouslySetInnerHTML */}
                    <div dangerouslySetInnerHTML={{ __html: msg.answerHtml }} style={{ marginTop: msg.fileElements ? '10px' : '0' }} />
                </>
            ) : msg.isHtml ? (
                /* For other HTML messages (tables, formatted text) */
                /* If you standardized on answerHtml, use that here too */
                <div dangerouslySetInnerHTML={{ __html: msg.answerHtml || msg.text }} />
            ) : (
                /* For plain text messages (user messages, errors) */
                typeof msg.text === 'string' ? msg.text.split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
                )) : msg.text
            )}
                  {/* {msg.isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                  ) : (
                    typeof msg.text === 'string' ? msg.text.split('\n').map((line, i, arr) => (
                        <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
                    )) : msg.text
                  )} */}
                  {/* The analytics toggle is a request for a chart, so the chart
                      is the answer - drawn here in the reply rather than hidden
                      behind a second click. */}
                  {msg.tableData && Array.isArray(msg.tableData) && msg.tableData.length > 0 && (
                    <ChatChart rows={msg.tableData} spec={msg.chartSpec} columns={msg.columnMeta} />
                  )}
                  {msg.tableData && Array.isArray(msg.tableData) && msg.tableData.length > 0 && ( // Check if tableData is valid
  <div style={{ marginTop: '10px', textAlign: 'left', display: 'flex', gap: '10px' }}>
    {/* Button to View Table */}
    <Button
      onClick={() => {
        console.log("View Table button clicked. tableData being passed to popup:", msg.tableData);
        setTablePopup({ open: true, data: msg.tableData });
      }}
      variant="contained" size="small"
      sx={{
        textTransform: 'none',
        fontSize: '13px',
        fontFamily:'Styrene',
        backgroundColor: '#4a4a4c',
        '&:hover': { backgroundColor: '#5a5a5e' }
      }}
    >
      View Table
    </Button>

    {/* Button to View Analytics */}
    <Button
      onClick={() => {
        setAnalyticsPopup({
          open: true,
          data: msg.tableData,
          title: msg.text,
          spec: msg.chartSpec || null,
          columns: msg.columnMeta || null,
        });
      }}
      variant="contained" size="small"
      sx={{
        textTransform: 'none',
        fontSize: '13px',
        fontFamily:'Styrene',
        backgroundColor: '#28a745', // Analytics button color
        '&:hover': { backgroundColor: '#218838' }
      }}
    >
      Explore Chart
    </Button>
  </div>
)}
                </div>
              </div>
            ))}
            {isThinking && thinkingSteps.length > 0 && <ThinkingBubble steps={thinkingSteps} />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div 
    className="chat-footer" 
     style={{ 
        /* Rectangular card like the second image */
       padding: 0, 
       backgroundColor: 'transparent',
       border: 'none',
       borderRadius: 0,
       width: '845px',            // keep if you still want a fixed width
       margin: '24px auto 16px',  // just some spacing; tweak as you like
 }}
>
         <div style={{ flex: 1, minHeight: 0 }}>
        <ChatInput 
          onSendMessage={handleSendMessage} 
          disabled={isThinking}
          onVoiceModeChange={setIsVoiceModeActive} 
          shouldTurnOffVoice={shouldTurnOffVoice}// New prop
          domain={domain}
          selectedTable={selectedTable}
          quickButtons={
       <ExpandableButtons
           domain={domain}
           selectedTable={selectedTable}
           onTableSelect={handleTableSelect}
        />
       }
        
/>
</div>


      </div>
      </>
      )}

      {/* <VoiceInteractionModal
        open={voiceModalOpen} onClose={() => setVoiceModalOpen(false)} domain={domain}
        table={selectedTable}
        onMessageLogged={(voiceMsgPayload) => {
            let finalMessageObject;
            const textContent = (typeof voiceMsgPayload === 'string' ? voiceMsgPayload : (voiceMsgPayload && typeof voiceMsgPayload.text === 'string' ? voiceMsgPayload.text : ''));

            if (typeof voiceMsgPayload === 'string') {
                finalMessageObject = { text: textContent, sender: 'user', isHtml: false, conversationId: conversationId };
            } else if (typeof voiceMsgPayload === 'object' && voiceMsgPayload.text !== undefined) {
                finalMessageObject = { ...voiceMsgPayload, text: textContent, sender: 'user', isHtml: false, conversationId: conversationId };
            } else {
                console.warn("Voice message payload was not in expected format:", voiceMsgPayload);
                return;
            }
            setMessages(prev => [...prev, finalMessageObject]);
            saveMessage(finalMessageObject, conversationId);
            if (!conversationStarted) setConversationStarted(true);
        }}
      /> */}
<PurchaseRequisitionPopup
  open={prPopup.open}
  data={prPopup.data}
  onClose={() => setPrPopup({ open: false, data: null, onSubmit: null })}
  onSubmit={prPopup.onSubmit}
/>
      <TablePopup
        open={tablePopup.open} data={tablePopup.data}
        onClose={() => setTablePopup({ open: false, data: null })}
        onDownload={() => {
            console.log("Download button in TablePopup clicked. Data in popup state:", tablePopup.data);
            downloadCSV(tablePopup.data);
        }}
      />
      <AnalyticsPopup
        open={analyticsPopup.open}
        data={analyticsPopup.data}
        title={analyticsPopup.title}
        spec={analyticsPopup.spec}
        columns={analyticsPopup.columns}
        onClose={() => setAnalyticsPopup({ open: false, data: null, title: '', spec: null, columns: null })}
      />

      <ChatHistoryPanel
        isOpen={isHistoryPanelOpen}
        onClose={handleToggleHistoryPanel}
        userId={hardcodedUserId}
      />
      
    </Paper>
  );
};

export default ChatInterface;
